'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { ArrowRight, Loader2, ShieldCheck, Timer, Zap } from 'lucide-react'

import { Container } from '@/components/Container'
import { FadeIn } from '@/components/FadeIn'

const NICHES = [
  { value: 'barbers-salons-spas', label: 'Barber / Salon / Spa' },
  { value: 'home-services', label: 'Home Services' },
  { value: 'restaurants-cafes', label: 'Restaurant / Cafe' },
  { value: 'med-dental-wellness', label: 'Medical / Dental / Wellness' },
  { value: 'other', label: 'Other' },
]

const SCAN_POINTS = [
  'Google Business Profile & reviews',
  'Booking & contact gaps',
  'Website speed & search visibility',
  'Social presence & brand consistency',
  'Advertising & revenue intelligence',
]

// Label is programmatically tied to the control (htmlFor/id) and errors are
// announced (role=alert) and associated (aria-describedby) — this is both the
// ARIA contract and what browser autofill heuristics key off.
function Field({ id, label, error, children }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-white/80">
        {label}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-red-400">
          {error.message}
        </p>
      )}
    </div>
  )
}

const inputClass =
  'w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base text-white placeholder-white/30 outline-none transition focus:border-kingdom-gold/60 focus:bg-white/[0.07]'

export default function DiagnosticIntakePage() {
  const router = useRouter()
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      url: '',
      businessName: '',
      city: '',
      state: '',
      niche: 'other',
      firstName: '',
    },
  })

  const onSubmit = async (data) => {
    setSubmitting(true)
    setServerError(null)
    try {
      const res = await fetch('/api/diagnostic/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Something went wrong')
      router.push(`/diagnostic/${json.uuid}`)
    } catch (err) {
      setServerError(err.message)
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-neutral-950 py-24 text-white sm:py-32">
      <Container>
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:items-center">
          <FadeIn>
            <p className="font-mono text-xs font-semibold tracking-[0.28em] text-kingdom-gold uppercase">
              Revenue Code Diagnostic · Free
            </p>
            <h1 className="mt-4 font-display text-4xl font-medium tracking-tight [text-wrap:balance] sm:text-5xl">
              Find the revenue leaking out of your business — in minutes.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-white/70">
              We scan your actual business — your site, your Google profile,
              your reviews, your booking flow — and show you exactly where
              customers are slipping away. Free, automated, no meeting
              required.
            </p>
            <ul className="mt-8 space-y-3">
              {SCAN_POINTS.map((point) => (
                <li key={point} className="flex items-center gap-3 text-white/80">
                  <Zap className="h-4 w-4 shrink-0 text-kingdom-gold" />
                  {point}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs text-white/50">
              <span className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-kingdom-gold" /> Results in ~2 minutes
              </span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-kingdom-gold" /> No credit card. No spam.
              </span>
            </div>
          </FadeIn>

          <FadeIn>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 shadow-[0_40px_120px_rgba(0,0,0,0.5)]"
            >
              <div className="space-y-5">
                {/* Explicit autocomplete tokens per WHATWG autofill spec —
                    without them browsers guess from placeholders and fill the
                    business name with the URL. */}
                <Field id="diag-url" label="Business website" error={errors.url}>
                  <input
                    id="diag-url"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="yourbusiness.com"
                    aria-invalid={errors.url ? 'true' : undefined}
                    aria-describedby={errors.url ? 'diag-url-error' : undefined}
                    className={inputClass}
                    {...register('url', { required: 'Enter your website URL' })}
                  />
                </Field>
                <Field id="diag-business" label="Business name" error={errors.businessName}>
                  <input
                    id="diag-business"
                    type="text"
                    autoComplete="organization"
                    placeholder="e.g. Fade Kings Barbershop"
                    aria-invalid={errors.businessName ? 'true' : undefined}
                    aria-describedby={errors.businessName ? 'diag-business-error' : undefined}
                    className={inputClass}
                    {...register('businessName', {
                      required: 'Enter your business name',
                    })}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field id="diag-city" label="City" error={errors.city}>
                    <input
                      id="diag-city"
                      type="text"
                      autoComplete="address-level2"
                      placeholder="Phoenix"
                      className={inputClass}
                      {...register('city')}
                    />
                  </Field>
                  <Field id="diag-state" label="State" error={errors.state}>
                    <input
                      id="diag-state"
                      type="text"
                      autoComplete="address-level1"
                      placeholder="AZ"
                      className={inputClass}
                      {...register('state')}
                    />
                  </Field>
                </div>
                <Field id="diag-niche" label="What kind of business?" error={errors.niche}>
                  <select id="diag-niche" autoComplete="off" className={inputClass} {...register('niche')}>
                    {NICHES.map((n) => (
                      <option key={n.value} value={n.value} className="bg-neutral-900">
                        {n.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="diag-first-name" label="Your first name (optional)" error={errors.firstName}>
                  <input
                    id="diag-first-name"
                    type="text"
                    autoComplete="given-name"
                    placeholder="Alex"
                    className={inputClass}
                    {...register('firstName')}
                  />
                </Field>
              </div>

              {serverError && (
                <p className="mt-4 text-sm text-red-400">{serverError}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-kingdom-gold px-6 py-4 text-base font-semibold text-neutral-950 transition hover:bg-[#d9bd65] disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Starting your scan…
                  </>
                ) : (
                  <>
                    Run My Free Diagnostic <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </button>
              <p className="mt-3 text-center text-xs text-white/40">
                Takes ~2 minutes. You&apos;ll see your preliminary results live.
              </p>
            </form>
          </FadeIn>
        </div>
      </Container>
    </div>
  )
}
